import {
  AppPermissions,
  TICKETING_SYSTEM_COMMON_PERMISSIONS
} from '@shared/constants/Mapping';
import {State} from '@shared/store/reducers/Index';
import {useSelector} from 'react-redux';

type ITicketingSystemPermissionFns = typeof TICKETING_SYSTEM_COMMON_PERMISSIONS[keyof typeof TICKETING_SYSTEM_COMMON_PERMISSIONS];

type ITicketingSystemPermissionObject = Record<
  ITicketingSystemPermissionFns,
  string
> &
  Record<string, string>;

type IPermissions = typeof AppPermissions;
type IAllModules = keyof IPermissions;

export type IAllPossibleTicketModules = {
  [K in IAllModules]: IPermissions[K] extends ITicketingSystemPermissionObject
    ? K
    : never;
}[IAllModules];

export type IGroupPermission = keyof IPermissions['MENU_PERMISSIONS'];

interface IUseTicketingSystemPermission<
  TModule extends IAllPossibleTicketModules
> {
  module: TModule;
  objectFunction: any;
  groupPermission?: IGroupPermission;
}

const useTicketingSystemPermission = <
  TModule extends IAllPossibleTicketModules
>(
  props: IUseTicketingSystemPermission<TModule>
): boolean => {
  const {userPermissions, groupId} = useSelector((state: State) => state.auth);
  const {module, objectFunction = 'LIST', groupPermission} = props;

  const doesUserHavePermission =
    !!groupId &&
    !!userPermissions?.[groupId]?.[AppPermissions[module].NAME]?.[
      AppPermissions[module][objectFunction]
    ];

  let isEnabledInGroup = true;
  if (groupPermission) {
    isEnabledInGroup =
      !!groupId &&
      !!userPermissions?.[groupId]?.['group']?.[
        AppPermissions.MENU_PERMISSIONS[groupPermission]
      ];
  }

  return doesUserHavePermission && isEnabledInGroup;
};

export default useTicketingSystemPermission;
